import type { LDContext, LDOptions } from 'launchdarkly-react-client-sdk';
import { LDProvider } from 'launchdarkly-react-client-sdk';
import type { PropsWithChildren } from 'react';
import { useMemo } from 'react';

import FeatureFlagsResolver from './FeatureFlagsResolver';

interface FeatureFlagsProviderProps {
  clientToken: string;
  async?: boolean;
  /**
   * When true, serves flags from localStorage on repeat visits while
   * fetching fresh values in the background. Eliminates LD from the
   * critical rendering path on warm loads.
   */
  useLocalStorageCache?: boolean;
  /**
   * When true, the SDK will not initialize until a `context` prop is
   * provided. Prevents a wasted anonymous-context fetch when the user
   * identity isn't known yet.
   */
  deferInitialization?: boolean;
  /**
   * Initial LaunchDarkly multi-context. When combined with
   * `deferInitialization`, the SDK initializes directly with this
   * context (one fetch instead of anonymous + identify).
   */
  context?: LDContext;
}

const FeatureFlagProvider = ({
  clientToken,
  async,
  useLocalStorageCache = false,
  deferInitialization = false,
  context,
  children,
}: PropsWithChildren<FeatureFlagsProviderProps>): JSX.Element => {
  const options: LDOptions | undefined = useMemo(
    () => (useLocalStorageCache ? { bootstrap: 'localStorage' } : undefined),
    [useLocalStorageCache]
  );

  return (
    <LDProvider
      clientSideID={clientToken}
      reactOptions={{ useCamelCaseFlagKeys: false }}
      options={options}
      deferInitialization={deferInitialization}
      context={context}
    >
      <FeatureFlagsResolver async={async}>{children}</FeatureFlagsResolver>
    </LDProvider>
  );
};

export default FeatureFlagProvider;
